/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import cloneDeep from 'lodash/cloneDeep'
import { APPROVAL_ACTION_TYPE, WF_STEP_PROCESSING_TYPE, WF_STEP_TYPE, WF_TPL_STEP } from './EditFormConstants'

const TYPE = {
  DATETIME: 'DATETIME',
  SELECT_ONE_USER: 'SELECT_ONE_USER',
  SELECT_ONE_GROUP: 'SELECT_ONE_GROUP',
  SELECT_MULTI_GROUP: 'SELECT_MULTI_GROUP',
  SELECT_MULTI_USER: 'SELECT_MULTI_USER'
}

export const transformToServer = (tpl: any) => {
  const transformedTpl = cloneDeep(tpl)

  // Transform initial approvers, collaborators, reviewers, approvers
  ;[WF_TPL_STEP.initialApprovers, WF_TPL_STEP.collaborators, WF_TPL_STEP.reviewers, WF_TPL_STEP.approvers].forEach(
    (key) => {
      transformedTpl[key]?.steps?.forEach((step: any) => {
        // Convert from {value, label} format back to {id} for server
        step.taskTeamStepLabel = step.taskTeamStepLabel ? { id: step.taskTeamStepLabel.value } : null

        step.taskTeams = []

        step.usersGroups?.forEach((stepItem: any) => {
          const stepItemArr = stepItem.split('-')
          step.taskTeams.push(
            stepItemArr[0] === 'GROUP'
              ? {
                  group: { id: stepItemArr[1] }
                }
              : {
                  user: { id: stepItemArr[1] }
                }
          )
        })

        step.usersGroups = undefined
      })
    }
  )

  // Transform approve actions
  transformedTpl[WF_TPL_STEP.approvers]?.approveActions?.forEach((action: any) => {
    if (!action.actionType) {
      return
    }
    action['@type'] = action.actionType
    action.notifies = []

    switch (action.actionType) {
      case APPROVAL_ACTION_TYPE.SET_PROPERTY:
        const newActionData: any[] = []

        action.actionData?.forEach((propActionRow: any) => {
          const actionDataRow: any = {
            metadataTemplateId: propActionRow.metadataTemplateId
          }

          if (propActionRow.property?.type === TYPE.DATETIME && propActionRow.val && propActionRow.val.unix) {
            actionDataRow.value = propActionRow.val.unix()
          } else if ([TYPE.SELECT_ONE_USER, TYPE.SELECT_ONE_GROUP].indexOf(propActionRow.property?.type) > -1) {
            const userSelectionObj = propActionRow.val.split('-')
            actionDataRow.value = JSON.stringify({ id: userSelectionObj[1], systemHelperType: userSelectionObj[0] })
          } else if ([TYPE.SELECT_MULTI_GROUP, TYPE.SELECT_MULTI_USER].indexOf(propActionRow.property?.type) > -1) {
            const actionDataRowValue = propActionRow.val.map((v: any) => {
              const userSelectionObj = v.split('-')
              return { id: userSelectionObj[1], systemHelperType: userSelectionObj[0] }
            })
            actionDataRow.value = JSON.stringify(actionDataRowValue)
          } else {
            actionDataRow.value = JSON.stringify(propActionRow.val)
          }

          newActionData.push(actionDataRow)
        })

        action.actionData = newActionData
        break
      case APPROVAL_ACTION_TYPE.COPY:
      case APPROVAL_ACTION_TYPE.MOVE:
        action.actionData = action.actionData.id
        break
    }

    action.usersGroups?.forEach((stepItem: any) => {
      const stepItemArr = stepItem.split('-')
      action.notifies.push(
        stepItemArr[0] === 'GROUP'
          ? {
              group: { id: stepItemArr[1] }
            }
          : {
              user: { id: stepItemArr[1] }
            }
      )
    })

    action.usersGroups = undefined
    action.actionType = undefined
  })

  // Transform trainees
  if (transformedTpl[WF_TPL_STEP.trainees]) {
    transformedTpl[WF_TPL_STEP.trainees].steps = transformedTpl[WF_TPL_STEP.trainees].usersGroups?.length
      ? [
          {
            processingType: WF_STEP_PROCESSING_TYPE.PARALLEL,
            taskTeams: []
          }
        ]
      : []

    transformedTpl[WF_TPL_STEP.trainees].usersGroups?.forEach((item: any) => {
      const usrGrpItemArr = item.split('-')
      transformedTpl[WF_TPL_STEP.trainees].steps[0].taskTeams.push(
        usrGrpItemArr[0] === 'GROUP'
          ? {
              group: { id: usrGrpItemArr[1] }
            }
          : {
              user: { id: usrGrpItemArr[1] }
            }
      )
    })

    transformedTpl[WF_TPL_STEP.trainees].usersGroups = undefined
  }

  // Transform rejection reviewers (with type field and N_OF_ALL processing)
  if (transformedTpl[WF_TPL_STEP.rejectionReviewers]) {
    transformedTpl[WF_TPL_STEP.rejectionReviewers].steps = transformedTpl[WF_TPL_STEP.rejectionReviewers].usersGroups
      ?.length
      ? [
          {
            processingType: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
            nofAllValue: 1,
            taskTeams: []
          }
        ]
      : []

    transformedTpl[WF_TPL_STEP.rejectionReviewers].usersGroups?.forEach((item: any) => {
      const usrGrpItemArr = item.split('-')
      transformedTpl[WF_TPL_STEP.rejectionReviewers].steps[0].taskTeams.push(
        usrGrpItemArr[0] === 'GROUP'
          ? {
              group: { id: usrGrpItemArr[1] }
            }
          : {
              user: { id: usrGrpItemArr[1] }
            }
      )
    })

    transformedTpl[WF_TPL_STEP.rejectionReviewers].usersGroups = undefined

    // Ensure type field is present (defaults to NOT_DEFINED if not set)
    if (!transformedTpl[WF_TPL_STEP.rejectionReviewers].type) {
      transformedTpl[WF_TPL_STEP.rejectionReviewers].type = WF_STEP_TYPE.NOT_DEFINED
    }
  }

  return transformedTpl
}

export const transformFromServer = (tpl: any, metadataTemplates: any[]) => {
  const transformedTpl = cloneDeep(tpl)

  // Transform initial approvers, collaborators, reviewers, approvers
  ;[WF_TPL_STEP.initialApprovers, WF_TPL_STEP.collaborators, WF_TPL_STEP.reviewers, WF_TPL_STEP.approvers].forEach(
    (key) => {
      transformedTpl[key]?.steps?.forEach((step: any) => {
        step.usersGroups = []

        // Transform taskTeamStepLabel from server format {id, text} to Select format {value, label}
        // Server sends: {id: number, text: string}
        // Convert to: {value: number, label: string}
        if (step.taskTeamStepLabel) {
          step.taskTeamStepLabel = {
            value: step.taskTeamStepLabel.id,
            label: step.taskTeamStepLabel.text || step.taskTeamStepLabel.label // Handle both formats
          }
        }

        step.taskTeams?.forEach((taskTeam: any) => {
          if (taskTeam.user) {
            step.usersGroups.push('USER-' + taskTeam.user.id)
          } else if (taskTeam.group) {
            step.usersGroups.push('GROUP-' + taskTeam.group.id)
          }
        })
      })
    }
  )

  // Transform approve actions
  transformedTpl[WF_TPL_STEP.approvers]?.approveActions?.forEach((action: any) => {
    action.actionType = action['@type']
    action.usersGroups = []

    switch (action.actionType) {
      case APPROVAL_ACTION_TYPE.SET_PROPERTY:
        action.actionData?.forEach((actionData: any) => {
          // Find the property from metadataTemplates array
          actionData.property = metadataTemplates.find((m: any) => m.id === actionData.metadataTemplateId)

          actionData.metadataTemplateType = actionData.property ? actionData.property.type : ''

          // Parse values based on property type
          if (actionData.property && actionData.property.type === TYPE.DATETIME) {
            // For DATETIME, parse as Unix timestamp
            actionData.val = { unix: () => actionData.value }
          } else {
            // For all other types, parse JSON
            actionData.val = JSON.parse(actionData.value)
          }

          // Transform SELECT_ONE and SELECT_MULTI options
          if (
            actionData.property &&
            (actionData.property.type === 'SELECT_ONE' || actionData.property.type === 'SELECT_MULTI')
          ) {
            actionData.property.definition.options = actionData.property.definition.options?.map((i: string) => ({
              label: i,
              value: i
            }))
          }

          // Transform SELECT_MULTI_USER and SELECT_MULTI_GROUP
          if (
            actionData.property &&
            (actionData.property.type === TYPE.SELECT_MULTI_USER ||
              actionData.property.type === TYPE.SELECT_MULTI_GROUP)
          ) {
            actionData.val = actionData.val.map((i: any) => i.systemHelperType + '-' + i.id)
          }

          // Transform SELECT_ONE_USER and SELECT_ONE_GROUP
          if (
            actionData.property &&
            (actionData.property.type === TYPE.SELECT_ONE_USER || actionData.property.type === TYPE.SELECT_ONE_GROUP)
          ) {
            actionData.val = actionData.val.systemHelperType + '-' + actionData.val.id
          }
        })
        break

      case APPROVAL_ACTION_TYPE.NOTIFY:
        action.actionData = action.customNotificationText || ''
        action.customNotificationText = undefined
        break

      case APPROVAL_ACTION_TYPE.COPY:
        action.actionData = { id: action.copyToFsiId, name: action.copyToFsiName }
        action.copyToFsiId = undefined
        action.copyToFsiName = undefined
        break

      case APPROVAL_ACTION_TYPE.MOVE:
        action.actionData = { id: action.moveToFsiId, name: action.moveToFsiName }
        action.moveToFsiId = undefined
        action.moveToFsiName = undefined
        break
    }

    // Transform notifies to usersGroups
    action.notifies?.forEach((stepItem: any) => {
      if (stepItem.user) {
        action.usersGroups.push('USER-' + stepItem.user.id)
      } else if (stepItem.group) {
        action.usersGroups.push('GROUP-' + stepItem.group.id)
      }
    })

    action.notifies = undefined
    action['@type'] = undefined
  })

  // Transform trainees and rejection reviewers
  ;[WF_TPL_STEP.trainees, WF_TPL_STEP.rejectionReviewers].forEach((key) => {
    if (transformedTpl[key]) {
      transformedTpl[key].usersGroups = []

      transformedTpl[key].steps?.forEach((step: any) => {
        step.taskTeams?.forEach((taskTeam: any) => {
          if (taskTeam.user) {
            transformedTpl[key].usersGroups.push('USER-' + taskTeam.user.id)
          } else if (taskTeam.group) {
            transformedTpl[key].usersGroups.push('GROUP-' + taskTeam.group.id)
          }
        })
      })
    }
  })

  return transformedTpl
}
